import React, { Fragment, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import Layout from '../../../Layout'
import BreadcrumbBlock from '../../../components/BreadcrumbBlock/BreadcrumbBlock'
import { Link } from 'react-router-dom'
import { Col, ConfigProvider, DatePicker, Row, TreeSelect } from 'antd'
import { getSLUnitByPermission } from '../../../api/selectOptions/selectOptionsApi'
import { convertNationwideValueToTree_V2 } from '../../../api/unit/unitApi'
import { useSelector } from 'react-redux'
import { selectorInfoUser } from '../../../store/slices/infoUserSlice'
import viVN from 'antd/locale/vi_VN';
import './styles.scss'
import { CaretDownOutlined, CaretRightOutlined } from '@ant-design/icons'



interface Period {
    key: string;
    label: string;
}

interface KPIItem {
    id: string;
    name: string;

    kpi: number;
    weight: number;
    average: number;

    periods: Record<
        string,
        {
            totalKH: number;
            rate: number;
        }
    >;
}

interface KPIGroup {
    id: string;
    name: string;
    children: KPIItem[];
}

const periods: Period[] = [
    { key: "T1", label: "T1" },
    { key: "T2", label: "T2" },
    { key: "T3", label: "T3" },
    { key: "T4", label: "T4" },
    { key: "T5", label: "T5" },
    { key: "T6", label: "T6" },
    { key: "T7", label: "T7" },
    { key: "T8", label: "T8" },
    { key: "T9", label: "T9" },
    { key: "T10", label: "T10" },
    { key: "T11", label: "T11" },
    { key: "T12", label: "T12" }
];

const groups: KPIGroup[] = [
    {
        id: "g1",
        name: "Thông tin định dạng",
        children: [
            {
                id: "1",
                name: "CCCD/Hộ chiếu",
                kpi: 80,
                weight: 15,
                average: 45,

                periods: {
                    T1: { totalKH: 1000, rate: 75 },
                    T2: { totalKH: 1500, rate: 75 },
                    T3: { totalKH: 2000, rate: 25 },
                    T4: { totalKH: 1000, rate: 25 },
                    T5: { totalKH: 1000, rate: 25 }
                }
            },

            {
                id: "2",
                name: "Mã số thuế",
                kpi: 80,
                weight: 10,
                average: 49,

                periods: {
                    T1: { totalKH: 1000, rate: 85 },
                    T2: { totalKH: 1500, rate: 85 },
                    T3: { totalKH: 2000, rate: 25 }
                }
            }
        ]
    },

    {
        id: "g2",
        name: "Thông tin liên hệ",
        children: [
            {
                id: "3",
                name: "Số điện thoại",
                kpi: 80,
                weight: 10,
                average: 47,

                periods: {
                    T1: { totalKH: 1000, rate: 60 },
                    T2: { totalKH: 1500, rate: 47 },
                    T3: { totalKH: 2000, rate: 47 }
                }
            },

            {
                id: "4",
                name: "Email",
                kpi: 50,
                weight: 5,
                average: 47,

                periods: {
                    T1: { totalKH: 1000, rate: 80 },
                    T2: { totalKH: 1500, rate: 99 },
                    T3: { totalKH: 2000, rate: 99 }
                }
            }
        ]
    }
];

// ─── Resize Handle ───────────────────────────────────────────────────────────
interface ResizeHandleProps {
    colKey: string;
    startWidth: number;
    minWidth?: number;
    maxWidth?: number;
    onWidthChange: (key: string, newWidth: number) => void;
}






const ResizeHandle: React.FC<ResizeHandleProps> = ({
    colKey, startWidth, minWidth = 60, maxWidth = 800, onWidthChange,
}) => {
    const onMouseDown = useCallback(
        (e: React.MouseEvent) => {
            e.preventDefault();
            e.stopPropagation();

            const startX = e.clientX;
            const origWidth = startWidth;

            const onMouseMove = (ev: MouseEvent) => {
                const dx = ev.clientX - startX;
                const next = Math.min(maxWidth, Math.max(minWidth, origWidth + dx));
                onWidthChange(colKey, next);
            };

            const onMouseUp = () => {
                window.removeEventListener('mousemove', onMouseMove);
                window.removeEventListener('mouseup', onMouseUp);
            };

            window.addEventListener('mousemove', onMouseMove);
            window.addEventListener('mouseup', onMouseUp);
        },
        [colKey, startWidth, minWidth, maxWidth, onWidthChange]
    );

    return <div className="col-resize-handle" onMouseDown={onMouseDown} />;
};

// ─── Default column widths ────────────────────────────────────────────────────
const DEFAULT_COL_WIDTHS = {
    c1: 260,
    c2: 110,
    c3: 130,
    c4: 130,
    ...Object.fromEntries(
        ['T1', 'T2', 'T3', 'T4', 'T5', 'T6', 'T7', 'T8', 'T9', 'T10', 'T11', 'T12'].flatMap(k => [
            [`${k}-kh`, 110],
            [`${k}-rate`, 100],
        ])
    ),
};

// ─── Hook: sync row heights between left & right table sections ──────────────
function useSyncSectionHeights(
    leftRef: React.RefObject<HTMLTableSectionElement | null>,
    rightRef: React.RefObject<HTMLTableSectionElement | null>,
    deps: any[]
) {
    const isSyncing = useRef(false);

    const sync = useCallback(() => {
        if (isSyncing.current) return;
        const lb = leftRef.current;
        const rb = rightRef.current;
        if (!lb || !rb) return;

        isSyncing.current = true;

        const lRows = Array.from(lb.rows);
        const rRows = Array.from(rb.rows);
        const count = Math.min(lRows.length, rRows.length);

        // Reset first
        for (let i = 0; i < lRows.length; i++) lRows[i].style.height = '';
        for (let i = 0; i < rRows.length; i++) rRows[i].style.height = '';

        // Measure natural heights and apply max
        for (let i = 0; i < count; i++) {
            const h = Math.max(lRows[i].getBoundingClientRect().height, rRows[i].getBoundingClientRect().height);
            lRows[i].style.height = `${h}px`;
            rRows[i].style.height = `${h}px`;
        }

        isSyncing.current = false;
    }, [leftRef, rightRef]);

    useLayoutEffect(() => {
        sync();
    }, deps);

    useEffect(() => {
        let rafId: number;
        const ro = new ResizeObserver(() => {
            cancelAnimationFrame(rafId);
            rafId = requestAnimationFrame(() => sync());
        });
        if (leftRef.current) ro.observe(leftRef.current);
        if (rightRef.current) ro.observe(rightRef.current);
        return () => {
            ro.disconnect();
            cancelAnimationFrame(rafId);
        };
    }, [sync, leftRef, rightRef]);
}






const TableReportDataByMonth = () => {


    const [colWidths, setColWidths] = useState<Record<string, number>>(DEFAULT_COL_WIDTHS);

    const setColWidth = useCallback((key: string, width: number) => {
        setColWidths(prev => ({ ...prev, [key]: width }));
    }, []);

    const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>({
        g1: true,
        g2: true,
    });

    // Refs for row-height sync
    const leftTheadRef = useRef<HTMLTableSectionElement>(null);
    const rightTheadRef = useRef<HTMLTableSectionElement>(null);
    const leftBodyRef = useRef<HTMLTableSectionElement>(null);
    const rightBodyRef = useRef<HTMLTableSectionElement>(null);

    // Sync thead & tbody row heights whenever data or widths change
    useSyncSectionHeights(leftTheadRef, rightTheadRef, [colWidths]);
    useSyncSectionHeights(leftBodyRef, rightBodyRef, [colWidths, expandedGroups]);

    const toggleGroup = (groupId: string) => {
        setExpandedGroups(prev => ({
            ...prev,
            [groupId]: !prev[groupId]
        }));
    };



    // Fixed panel width = sum of 4 left columns
    const fixedWidth = colWidths.c1 + colWidths.c2 + colWidths.c3 + colWidths.c4;

    // Right table total width
    const rightTableWidth = periods.reduce(
        (sum, p) => sum + (colWidths[`${p.key}-kh`] ?? 110) + (colWidths[`${p.key}-rate`] ?? 100),
        0
    );





    return (
        <div className='custom-page-css flex flex-col gap-2'>
            <div className="custom-page-css flex flex-col ">
                <span className='custom-page-css text-344054 font-medium text-base'>Báo cáo chất lượng dữ liệu theo tháng</span>
                <span className='custom-page-css text-344054 font-regular text-sm text-italic'>Người dùng có thể click chuột trực tiếp vào tỷ lệ của tháng gần nhất để kiểm tra chi tiết thông tin.</span>
            </div>

            {/* ── Dual-table layout ── */}
            <div className="split-table-wrapper">

                {/* LEFT: fixed panel */}
                <div className="split-table-left" style={{ width: fixedWidth, minWidth: fixedWidth }}>
                    <table
                        className="kpi-table kpi-table-left"
                        style={{ width: fixedWidth }}
                    >
                        <colgroup>
                            <col style={{ width: colWidths.c1 }} />
                            <col style={{ width: colWidths.c2 }} />
                            <col style={{ width: colWidths.c3 }} />
                            <col style={{ width: colWidths.c4 }} />
                        </colgroup>
                        <thead ref={leftTheadRef}>
                            <tr>
                                <th rowSpan={2}>
                                    Hạng mục
                                    <ResizeHandle colKey="c1" startWidth={colWidths.c1} onWidthChange={setColWidth} />
                                </th>
                                <th rowSpan={2}>
                                    KPI
                                    <ResizeHandle colKey="c2" startWidth={colWidths.c2} onWidthChange={setColWidth} />
                                </th>
                                <th rowSpan={2}>
                                    Trọng số (%)
                                    <ResizeHandle colKey="c3" startWidth={colWidths.c3} onWidthChange={setColWidth} />
                                </th>
                                <th rowSpan={2} className="last-fixed-col">
                                    Trung bình (%)
                                    <ResizeHandle colKey="c4" startWidth={colWidths.c4} onWidthChange={setColWidth} />
                                </th>
                            </tr>
                            {/* 2nd header row placeholder to match right table */}
                            <tr />
                        </thead>

                        <tbody ref={leftBodyRef}>
                            {groups.map(group => {
                                const expanded = expandedGroups[group.id];
                                return (
                                    <Fragment key={group.id}>
                                        {/* GROUP ROW */}
                                        <tr className="group-row">
                                            <td
                                                className="group-title"
                                                colSpan={4}
                                                onClick={() => toggleGroup(group.id)}
                                            >
                                                <div style={{ display: 'flex', alignItems: 'center', gap: 10, height: '100%' }}>

                                                    {
                                                        expanded ? <CaretDownOutlined />
                                                            :
                                                            <CaretRightOutlined />
                                                    }
                                                    {group.name}
                                                </div>
                                            </td>
                                        </tr>

                                        {/* CHILD ROWS */}
                                        {expanded && group.children.map(item => (
                                            <tr key={item.id} className="data-row">
                                                <td className="text-left">{item.name}</td>
                                                <td>≥ {item.kpi}%</td>
                                                <td>{item.weight}%</td>
                                                <td className="last-fixed-col">{item.average}%</td>
                                            </tr>
                                        ))}
                                    </Fragment>
                                );
                            })}
                        </tbody>
                    </table>
                </div>

                {/* RIGHT: scrollable panel */}
                <div className="split-table-right">
                    <table
                        className="kpi-table kpi-table-right"
                        style={{ width: rightTableWidth }}
                    >
                        <colgroup>
                            {periods.flatMap(p => [
                                <col key={`${p.key}-kh`} style={{ width: colWidths[`${p.key}-kh`] }} />,
                                <col key={`${p.key}-rate`} style={{ width: colWidths[`${p.key}-rate`] }} />,
                            ])}
                        </colgroup>
                        <thead ref={rightTheadRef}>
                            <tr>
                                {periods.map(period => (
                                    <th key={period.key} colSpan={2}>
                                        {period.label}
                                    </th>
                                ))}
                            </tr>
                            <tr>
                                {periods.map(period => (
                                    <Fragment key={period.key}>
                                        <th>
                                            Tổng KH
                                            <ResizeHandle colKey={`${period.key}-kh`} startWidth={colWidths[`${period.key}-kh`]} onWidthChange={setColWidth} />
                                        </th>
                                        <th>
                                            Tỷ lệ
                                            <ResizeHandle colKey={`${period.key}-rate`} startWidth={colWidths[`${period.key}-rate`]} onWidthChange={setColWidth} />
                                        </th>
                                    </Fragment>
                                ))}
                            </tr>
                        </thead>

                        <tbody ref={rightBodyRef}>
                            {groups.map(group => {
                                const expanded = expandedGroups[group.id];
                                return (
                                    <Fragment key={group.id}>
                                        {/* GROUP ROW — empty fill */}
                                        <tr className="group-row">
                                            <td colSpan={periods.length * 2} className="group-fill" />
                                        </tr>

                                        {/* CHILD ROWS */}
                                        {expanded && group.children.map(item => (
                                            <tr key={item.id} className="data-row">
                                                {periods.flatMap(period => {
                                                    const value = item.periods[period.key];
                                                    return [
                                                        <td key={`${item.id}-${period.key}-kh`}>
                                                            {value ? value.totalKH.toLocaleString("vi-VN") : "--"}
                                                        </td>,
                                                        <td
                                                            key={`${item.id}-${period.key}-rate`}
                                                            className={value?.rate < item.kpi ? "danger" : ""}
                                                        >
                                                            {value ? `${value.rate}%` : "--"}
                                                        </td>
                                                    ];
                                                })}
                                            </tr>
                                        ))}
                                    </Fragment>
                                );
                            })}
                        </tbody>
                    </table>
                </div>

            </div>
        </div>
    )
}

export default TableReportDataByMonth